import { Component, ViewChild, ElementRef, Input } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { FormControl, Validators } from '@angular/forms';
import {
  AuthorizeNetTransaction,
  DesktopPaymentDetails,
  DesktopPaymentsService,
} from 'src/app/services/desktop-payments.service';
import {
  GroupedLicenseInvoiceDetails,
  GroupedLicenses,
  DesktopInvoiceDetails,
  InvoicesService,
  ApiInvoiceDetails,
} from 'src/app/services/invoices.service';
import {
  DesktopLicenseWithProduct,
  LicensesService,
} from 'src/app/services/licenses.service';
import { Tenant } from 'src/app/services/tenants.service';
import { environment } from 'src/environments/environment';
import { SalesRep } from 'src/app/services/sales-reps.service';
import { ViewService } from 'src/app/services/view.service';
import { ToastService } from 'src/app/services/toast.service';
import { ApiProduct } from 'src/app/services/api-products.service';
import {
  ApiPaymentDetails,
  ApiPaymentsService,
} from 'src/app/services/api-payments.service';
import {
  DesCreditPurchase,
  DesCreditPurchasesService,
  DesPaymentDetails,
} from 'src/app/services/des-credit-purchases.service';

@Component({
  selector: 'app-receipt',
  templateUrl: './receipt.component.html',
  styleUrl: './receipt.component.scss',
})
export class ReceiptComponent {
  @ViewChild('emailReceiptDialog')
  emailReceiptDialog!: ElementRef<HTMLDialogElement>;

  @Input({ required: true }) invoiceId!: string;
  @Input({ required: true }) tenantId!: string;
  @Input() isAPI?: boolean;
  @Input() isDES?: boolean;

  desktopInvoice?: DesktopInvoiceDetails;
  desktopPaymentDetails?: DesktopPaymentDetails;

  apiInvoice?: ApiInvoiceDetails;
  apiProduct?: ApiProduct;
  apiPaymentDetails?: ApiPaymentDetails;

  desPurchase?: DesCreditPurchase;
  desPaymentDetails?: DesPaymentDetails;

  successfulPayment?: AuthorizeNetTransaction;

  loadingInvoice = false;
  loadingPaymentDetails = false;
  errorFetchingInvoice = '';
  errorFetchingPaymentDetails = '';

  tenant?: Tenant;

  groupedLicenses: GroupedLicenseInvoiceDetails[] = [];
  groupedNotRenewingLicenses: GroupedLicenses[] = [];

  customEmail = new FormControl('', [Validators.email]);
  emailError = '';
  emailSending = false;
  selectedEmails: string[] = [];
  additionalEmails: string[] = [];
  isAddingEmail = false;
  @ViewChild('emailInput') emailInput?: ElementRef<HTMLInputElement>;

  get allEmails(): string[] {
    return Array.from(
      new Set<string>([
        ...(this.tenant?.emails || []),
        ...(this.tenant?.billing_emails || []),
        ...this.additionalEmails,
      ])
    );
  }

  get accountEmails(): string[] {
    return this.tenant?.emails || [];
  }

  get billingEmails(): string[] {
    return this.tenant?.billing_emails || [];
  }

  get displayPaymentMethod(): string {
    let method = '';
    if (this.desktopPaymentDetails) {
      method = this.desktopPaymentDetails.payment_method;
    }
    if (this.apiPaymentDetails) {
      method = this.apiPaymentDetails.payment_method;
    }
    if (this.desPaymentDetails) {
      method = this.desPaymentDetails.payment_method;
    }

    method = method.replace('eCheck', 'ACH');
    return method;
  }

  constructor(
    private authService: AuthService,
    private apiPaymentsService: ApiPaymentsService,
    private desktopPaymentsService: DesktopPaymentsService,
    private invoiceService: InvoicesService,
    private licensesService: LicensesService,
    private toastService: ToastService,
    private viewService: ViewService,
    private desCreditPurchasesService: DesCreditPurchasesService
  ) {}

  ngOnInit() {
    if (!this.invoiceId) {
      this.errorFetchingInvoice = 'Error fetching invoice. Parameter missing.';
      console.error(new Error('Invoice ID is not defined'));
      return;
    }

    if (this.isDES) {
      this.loadDesPurchase(this.invoiceId);
      this.loadDesPaymentDetails(this.invoiceId);
      return;
    }

    // If isAPI is explicitly set, use it. Otherwise, try to determine the type
    if (this.isAPI === true) {
      this.loadApiInvoice(this.invoiceId);
      this.loadApiPaymentDetails(this.invoiceId);
    } else if (this.isAPI === false) {
      this.loadInvoice(this.invoiceId);
      this.loadPaymentDetails(this.invoiceId);
    } else {
      // Try desktop invoice first, then API if it fails
      this.determineInvoiceType();
    }
  }

  determineInvoiceType() {
    if (!this.invoiceId) return;

    // Try desktop invoice first
    this.invoiceService.getInvoiceDetails(this.invoiceId).subscribe({
      next: (res) => {
        // Desktop invoice found
        this.isAPI = false;
        this.loadInvoice(this.invoiceId);
        this.loadPaymentDetails(this.invoiceId);
      },
      error: (err: any) => {
        if (err.status === 404) {
          // Desktop invoice not found, try API invoice
          this.invoiceService.getApiInvoiceDetails(this.invoiceId).subscribe({
            next: () => {
              // API invoice found
              this.isAPI = true;
              this.loadApiInvoice(this.invoiceId);
            },
            error: () => {
              // Neither found
              this.loadingInvoice = false;
              this.errorFetchingInvoice = 'Invoice not found.';
            },
          });
        } else {
          this.loadingInvoice = false;
          this.errorFetchingInvoice = 'Error fetching invoice.';
        }
      },
    });
  }

  get currentView() {
    return this.viewService.currentView;
  }

  get quickbooksInvoiceId() {
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.quickbooks_invoice_id;
    }
    if (this.apiInvoice) {
      return this.apiInvoice.api_invoice.quickbooks_invoice_id;
    }
    return this.desPurchase?.quickbooks_invoice_id;
  }

  get quickbooksPaymentId() {
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.quickbooks_payment_id;
    }
    if (this.apiInvoice) {
      return this.apiInvoice.api_invoice.quickbooks_payment_id;
    }
    return this.desPurchase?.quickbooks_payment_id;
  }

  get hasOrder(): boolean {
    return !!(
      (this.desktopInvoice && this.desktopPaymentDetails && this.tenant) ||
      (this.apiInvoice &&
        this.apiPaymentDetails &&
        this.apiProduct &&
        this.tenant) ||
      (this.desPurchase && this.desPaymentDetails && this.tenant)
    );
  }

  get orderNumber(): string {
    if (this.desPurchase) {
      return this.desPurchase.number;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.number;
    }
    return this.apiInvoice!.api_invoice.number;
  }

  get orderTotal(): number {
    if (this.desPurchase) {
      return this.desPurchase.total_amount;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.total_amount;
    }
    return this.apiInvoice!.api_invoice.total_amount;
  }

  get orderBase(): number {
    if (this.desPurchase) {
      return this.desPurchase.base_amount;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.base_amount;
    }
    return this.apiInvoice!.api_invoice.base_amount;
  }

  get orderTax(): number {
    if (this.desPurchase) {
      return this.desPurchase.tax_amount;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.tax_amount;
    }
    return this.apiInvoice!.api_invoice.tax_amount;
  }

  get orderCompletedAt(): string | undefined {
    if (this.desPurchase) {
      return this.desPurchase.completed_at;
    }
    if (this.desktopInvoice) {
      return this.desktopInvoice.desktop_invoice.completed_at;
    }
    return this.apiInvoice!.api_invoice.completed_at;
  }

  get desPricePerThousand(): number {
    if (!this.desPurchase || !this.desPurchase.credits) {
      return 0;
    }
    return this.desPurchase.base_amount / (this.desPurchase.credits / 1000);
  }

  refreshInvoice() {
    if (!this.invoiceId) {
      this.errorFetchingInvoice = 'Error fetching invoice. Parameter missing.';
      console.error(new Error('Invoice ID is not defined'));
      return;
    }
    if (this.isDES) {
      this.loadDesPurchase(this.invoiceId);
      return;
    }
    if (this.isAPI === true) {
      this.loadApiInvoice(this.invoiceId);
    } else if (this.isAPI === false) {
      this.loadInvoice(this.invoiceId);
    } else {
      this.determineInvoiceType();
    }
  }

  loadInvoice(invoiceId: string) {
    this.loadingInvoice = true;

    this.invoiceService.getInvoiceDetails(invoiceId).subscribe({
      next: (res) => {
        this.loadingInvoice = false;
        console.log('Invoice details: ', res);
        this.desktopInvoice = res.invoice_details;
        this.tenant = res.tenant;

        this.groupedLicenses =
          this.invoiceService.groupLicenseDetailsByRelation(
            this.desktopInvoice.license_details
          );
        this.groupedNotRenewingLicenses =
          this.invoiceService.groupLicensesByProduct(
            this.desktopInvoice.not_renewing_licenses
          );
      },
      error: (err) => {
        this.loadingInvoice = false;
        console.error(err);
        this.errorFetchingInvoice = err;
      },
    });
  }

  loadApiInvoice(invoiceId: string) {
    this.loadingInvoice = true;

    this.invoiceService.getApiInvoiceDetails(invoiceId).subscribe({
      next: (res) => {
        this.loadingInvoice = false;
        console.log('API Invoice details: ', res);
        this.apiInvoice = res.api_invoice_details;
        this.tenant = res.tenant;
        this.apiProduct = res.api_invoice_details.api_product;
      },
      error: (err) => {
        this.loadingInvoice = false;
        console.error(err);
        this.errorFetchingInvoice = err;
      },
    });
  }

  loadPaymentDetails(invoiceId: string) {
    this.desktopPaymentsService.getDesktopPaymentDetails(invoiceId).subscribe({
      next: (res) => {
        console.log('Payment details: ', res.payment_details);
        this.desktopPaymentDetails = res.payment_details;
        this.setSuccessfulPayment();
      },
      error: (err) => {
        console.error(err);
        this.errorFetchingPaymentDetails = err;
      },
    });
  }

  loadApiPaymentDetails(invoiceId: string) {
    this.apiPaymentsService.getApiPaymentDetails(invoiceId).subscribe({
      next: (res) => {
        console.log('API Payment details: ', res);
        this.apiPaymentDetails = res.api_payment_details;
        this.setSuccessfulPayment();
      },
      error: (err) => {
        console.error(err);
        this.errorFetchingPaymentDetails = err;
      },
    });
  }

  loadDesPurchase(purchaseId: string) {
    this.loadingInvoice = true;
    this.desCreditPurchasesService.getById(purchaseId).subscribe({
      next: (res) => {
        this.loadingInvoice = false;
        this.desPurchase = res.des_credit_purchase;
        this.tenant = res.tenant;
      },
      error: (err) => {
        this.loadingInvoice = false;
        console.error(err);
        this.errorFetchingInvoice = err;
      },
    });
  }

  loadDesPaymentDetails(purchaseId: string) {
    this.loadingPaymentDetails = true;
    this.desCreditPurchasesService.getPaymentDetails(purchaseId).subscribe({
      next: (res) => {
        this.loadingPaymentDetails = false;
        this.desPaymentDetails = res.des_payment_details;
        this.setSuccessfulPayment();
      },
      error: (err) => {
        this.loadingPaymentDetails = false;
        console.error(err);
        this.errorFetchingPaymentDetails = err;
      },
    });
  }

  setSuccessfulPayment() {
    if (
      !this.desktopPaymentDetails &&
      !this.apiPaymentDetails &&
      !this.desPaymentDetails
    ) {
      return;
    }
    let successfulPayment: AuthorizeNetTransaction | undefined;
    if (this.desktopPaymentDetails) {
      successfulPayment =
        this.desktopPaymentsService.getSuccessfulAuthorizeNetTransaction(
          this.desktopPaymentDetails.transactions
        );
    }
    if (this.apiPaymentDetails) {
      successfulPayment =
        this.desktopPaymentsService.getSuccessfulAuthorizeNetTransaction(
          this.apiPaymentDetails.transactions
        );
    }
    if (this.desPaymentDetails) {
      successfulPayment =
        this.desktopPaymentsService.getSuccessfulAuthorizeNetTransaction(
          this.desPaymentDetails.transactions || []
        );
    }
    if (successfulPayment) {
      this.successfulPayment = successfulPayment;
    }
  }

  getDisplayTransactionMonth(dateString: string): Date {
    return this.invoiceService.getDisplayTransactionMonth(dateString);
  }

  getLicenseType(license: DesktopLicenseWithProduct) {
    return this.licensesService.getType(license);
  }

  getPrice(license: DesktopLicenseWithProduct) {
    return this.invoiceService.getLicensePrice(license);
  }

  getSalesRepNames(salesReps?: SalesRep[]) {
    if (!salesReps || salesReps.length === 0) {
      return 'House';
    }
    return salesReps.map((r) => r.name).join(', ');
  }

  printReceipt() {
    window.print();
  }

  get isAdmin() {
    return this.authService.isAdmin();
  }

  getQuickbooksInvoiceLink(invoiceId: string) {
    if (environment.production) {
      return 'https://qbo.intuit.com/app/invoice?txnId=' + invoiceId;
    } else {
      return 'https://sandbox.qbo.intuit.com/app/invoice?txnId=' + invoiceId;
    }
  }

  getQuickbooksPaymentLink(paymentId: string) {
    //sandbox.qbo.intuit.com/app/recvpayment?txnId=185
    if (environment.production) {
      return 'https://qbo.intuit.com/app/recvpayment?txnId=' + paymentId;
    } else {
      return (
        'https://sandbox.qbo.intuit.com/app/recvpayment?txnId=' + paymentId
      );
    }
  }

  openAuthorizeNetPayment(transId: string) {
    // copy the transId to the clipboard
    navigator.clipboard.writeText(transId);

    if (environment.production) {
      window.open(
        'https://account.authorize.net/ui/themes/anet/merch.aspx?page=search&sub=batchlist',
        '_blank'
      );
    } else {
      window.open(
        'https://sandbox.authorize.net/UI/themes/sandbox/merch.aspx?page=search&sub=batchlist',
        '_blank'
      );
    }
  }

  openEmailReceiptDialog() {
    this.emailReceiptDialog.nativeElement.showModal();
  }

  closeEmailReceiptDialog() {
    this.emailError = '';
    this.emailSending = false;
    this.selectedEmails = [];
    this.isAddingEmail = false;
    this.customEmail.reset();
    this.emailReceiptDialog.nativeElement.close();
  }

  toggleEmail(email: string) {
    const index = this.selectedEmails.indexOf(email);
    if (index === -1) {
      this.selectedEmails.push(email);
    } else {
      this.selectedEmails.splice(index, 1);
    }
    this.emailError = '';
  }

  startAddingEmail() {
    this.isAddingEmail = true;
    // Focus the input after the view updates
    setTimeout(() => {
      this.emailInput?.nativeElement.focus();
    });
  }

  cancelAddingEmail() {
    this.isAddingEmail = false;
    this.customEmail.reset();
    this.emailError = '';
  }

  addCustomEmail() {
    const email = this.customEmail.value?.trim();
    if (email && this.customEmail.valid) {
      if (this.allEmails.includes(email)) {
        this.emailError = 'This email already exists in the list';
        return;
      }

      this.additionalEmails.push(email);
      this.selectedEmails.push(email);
      this.customEmail.reset();
      this.emailError = '';
      this.isAddingEmail = false;
    } else {
      this.emailError = 'Please enter a valid email address';
    }
  }

  isValidEmail(): boolean {
    return this.selectedEmails.length > 0;
  }

  sendReceipt() {
    this.emailSending = true;

    if (!this.isValidEmail()) {
      this.emailError = 'Please select or enter at least one email address';
      this.emailSending = false;
      return;
    }

    if (!this.invoiceId) {
      this.emailError = 'Error: Invoice ID is missing';
      this.emailSending = false;
      return;
    }

    const emailObservable = this.isDES
      ? this.desCreditPurchasesService.sendReceiptEmail(
          this.invoiceId,
          this.selectedEmails
        )
      : this.isAPI
        ? this.invoiceService.sendApiReceiptEmail(
            this.invoiceId,
            this.selectedEmails
          )
        : this.invoiceService.sendReceiptEmail(
            this.invoiceId,
            this.selectedEmails
          );

    emailObservable.subscribe({
      next: (res) => {
        console.log('Payment receipt email sent: ', res);
        this.emailSending = false;
        this.emailError = '';

        this.closeEmailReceiptDialog();
        this.toastService.addToast('success', 'Emails sent successfully!');
      },
      error: (err) => {
        console.error('Error sending receipt email:', err);
        this.emailError = 'Failed to send email. Please try again.';
        this.emailSending = false;
      },
    });
  }
}
